import React from 'react';
import { TimelineData, TimelineArticle } from '../../types/timeline';
import { SupportedLanguage } from '../../types/language';
import { NewspaperIcon } from '@heroicons/react/24/outline';
import { ArticleCard } from './ArticleCard';
import { useTranslation } from 'react-i18next';

interface RecentArticlesCardProps {
  timelineData: TimelineData;
  timeRange: string;
  selectedCategories: Set<string>;
  viewMode: 'detailed' | 'simplified';
  language: SupportedLanguage;
  onArticleSelect: (article: TimelineArticle) => void;
  onShowAllCategories: () => void;
}

export const RecentArticlesCard: React.FC<RecentArticlesCardProps> = ({
  timelineData,
  timeRange,
  selectedCategories,
  viewMode,
  language,
  onArticleSelect,
  onShowAllCategories,
}) => {
  const isMilitaryMode = selectedCategories.size === 1 && selectedCategories.has('MILITARY');
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-2xl shadow-lg p-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        {isMilitaryMode 
          ? `🎯 ${t('public.articles.militaryThreats')} (${timeRange})` 
          : `${t('public.recentArticles')} (${timeRange})`
        }
      </h3>
      
      {isMilitaryMode && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-sm text-red-700">
            <strong>{t('public.articles.focus')}:</strong> {t('public.articles.focusDescription')}
          </div>
        </div>
      )}
      
      <div className="space-y-3">
        {(() => {
          // Get all articles from the selected time range - filtering now handled server-side
          const allArticles = timelineData.timeline
            .flatMap(day => day.articles || [])
            .sort((a, b) => b.overallRiskScore - a.overallRiskScore)
            .slice(0, viewMode === 'simplified' ? 3 : 5);
          
          if (allArticles.length === 0) {
            return (
              <div className="text-center py-8">
                <NewspaperIcon className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500 text-sm">
                  {isMilitaryMode
                    ? `${t('public.articles.noThreatsFound')} ${timeRange}`
                    : `${t('public.articles.noArticlesFound')} ${timeRange}`
                  }
                </p>
                {isMilitaryMode && (
                  <button
                    onClick={onShowAllCategories}
                    className="mt-2 text-xs text-blue-600 hover:text-blue-700 underline"
                  >
                    {t('public.articles.showAllCategories')}
                  </button>
                )}
              </div>
            );
          }
          
          return allArticles.map((article: TimelineArticle) => (
            <ArticleCard
              key={article.id}
              article={article}
              isMilitaryMode={isMilitaryMode}
              language={language}
              onArticleSelect={onArticleSelect}
            />
          ));
        })()}
      </div>
    </div>
  );
};
